import type { BreadcrumbItem } from '../types/breadcrumb'

export type DisplayBreadcrumbItem = BreadcrumbItem & {
  displayLabel: string
  isEllipsis?: boolean
  /** 段内文案末尾省略（CSS truncate），完整内容见 title */
  ellipsize?: boolean
}

export type BreadcrumbLayoutResult = {
  items: DisplayBreadcrumbItem[]
  /** 非末段启用末尾省略 */
  ellipsizeMiddle: boolean
  /** 末段（当前页）也启用末尾省略 */
  ellipsizeLast: boolean
}

const HOME_LABEL = '首页'
const MEASURE_ROW_CLASS = 'flex flex-nowrap items-center gap-x-1.5'
const MEASURE_TEXT_CLASS_DEFAULT = 'whitespace-nowrap text-[11px] font-medium sm:text-xs'
const MEASURE_TEXT_CLASS_DENSE = 'whitespace-nowrap text-sm font-medium'
const MEASURE_WIDTH_PADDING_PX = 6

function toDisplayItem(item: BreadcrumbItem, ellipsize = false): DisplayBreadcrumbItem {
  return {
    ...item,
    displayLabel: item.label.trim(),
    title: item.title ?? item.label,
    ellipsize,
  }
}

function isHomeItem(item: BreadcrumbItem | undefined): boolean {
  return item?.label.trim() === HOME_LABEL
}

function moduleAnchorIndex(items: BreadcrumbItem[]): number {
  if (items.length === 0) return 0
  if (isHomeItem(items[0]) && items.length > 1) return 1
  return 0
}

/**
 * 构造面包屑行：可选隐藏首页；始终保留模块根（图库/漫画等）与末尾 tailCount 段（含当前页）；
 * 中间被跳过的段以「…」表示。
 */
export function buildBreadcrumbLayout(
  items: BreadcrumbItem[],
  options: { omitHome: boolean; tailCount: number },
): DisplayBreadcrumbItem[] {
  if (items.length === 0) return []

  const tailCount = Math.max(1, Math.min(options.tailCount, items.length))
  const tailStart = items.length - tailCount
  const tailIndices = new Set<number>(
    Array.from({ length: tailCount }, (_, offset) => tailStart + offset),
  )

  const out: DisplayBreadcrumbItem[] = []
  const used = new Set<number>()

  const pushIndex = (index: number) => {
    if (used.has(index)) return
    used.add(index)
    out.push(toDisplayItem(items[index]!))
  }

  if (isHomeItem(items[0]) && !options.omitHome) {
    pushIndex(0)
  }

  const moduleIdx = moduleAnchorIndex(items)
  if (!tailIndices.has(moduleIdx)) {
    pushIndex(moduleIdx)
  }

  const hidden: BreadcrumbItem[] = []
  for (let i = 0; i < tailStart; i++) {
    if (!used.has(i) && !tailIndices.has(i)) {
      hidden.push(items[i]!)
    }
  }

  if (hidden.length > 0) {
    out.push({
      label: '…',
      displayLabel: '…',
      isEllipsis: true,
      title: hidden.map((item) => item.label).join(' / '),
    })
  }

  for (let i = tailStart; i < items.length; i++) {
    pushIndex(i)
  }

  return out
}

/** 离屏测量一行面包屑占位宽度（完整文案、不换行） */
export function measureBreadcrumbRowWidth(
  displayItems: DisplayBreadcrumbItem[],
  dense: boolean,
): number {
  if (typeof document === 'undefined' || displayItems.length === 0) return 0

  const row = document.createElement('div')
  row.className = MEASURE_ROW_CLASS
  row.style.cssText = 'position:fixed;left:-10000px;top:0;visibility:hidden;pointer-events:none'

  const textClass = dense ? MEASURE_TEXT_CLASS_DENSE : MEASURE_TEXT_CLASS_DEFAULT

  for (let i = 0; i < displayItems.length; i++) {
    if (i > 0) {
      const sep = document.createElement('span')
      sep.className = 'shrink-0 font-normal'
      sep.textContent = '/'
      row.appendChild(sep)
    }
    const item = displayItems[i]!
    const el = document.createElement('span')
    el.className = textClass
    el.textContent = item.displayLabel
    row.appendChild(el)
  }

  document.body.appendChild(row)
  const width = row.getBoundingClientRect().width
  document.body.removeChild(row)
  return width
}

/**
 * 在可用宽度内选取最「完整」的布局：先全路径，再藏首页，再减少末尾路径段，最后才段内末尾省略。
 */
export function pickBreadcrumbLayout(
  items: BreadcrumbItem[],
  availableWidth: number,
  dense: boolean,
): BreadcrumbLayoutResult {
  const full: BreadcrumbLayoutResult = {
    items: items.map((item) => toDisplayItem(item)),
    ellipsizeMiddle: false,
    ellipsizeLast: false,
  }

  if (items.length === 0 || availableWidth <= 0) return full

  const budget = Math.max(0, availableWidth - MEASURE_WIDTH_PADDING_PX)
  const fits = (layoutItems: DisplayBreadcrumbItem[]) =>
    measureBreadcrumbRowWidth(layoutItems, dense) <= budget

  if (fits(full.items)) return full

  for (const omitHome of [false, true]) {
    if (omitHome && !isHomeItem(items[0])) continue

    for (let tailCount = items.length; tailCount >= 1; tailCount--) {
      const layoutItems = buildBreadcrumbLayout(items, { omitHome, tailCount })
      if (fits(layoutItems)) {
        return {
          items: layoutItems,
          ellipsizeMiddle: false,
          ellipsizeLast: false,
        }
      }
    }
  }

  const fallbackItems = buildBreadcrumbLayout(items, {
    omitHome: true,
    tailCount: Math.min(2, items.length),
  })

  return {
    items: fallbackItems,
    ellipsizeMiddle: true,
    ellipsizeLast: true,
  }
}
